import { useConnection } from "@solana/wallet-adapter-react";
import { WRAPPED_SOL_MINT } from "@stepfinance/common";
import type { NFTEscrowAccount, NFTPoolOrder, NFTSingleOrder } from "@stepfinance/nft";
import { TensorOrdersFetcher } from "@stepfinance/nft";
import { useEffect, useMemo, useState } from "react";

import { useMarkets } from "../../contexts/market";
import { useConnectedWallet } from "../wallet";

const handleTensorError = (error) => console.error("Tensor Error:", error);

export const useTensorOrders = () => {
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const [openBids, setOpenBids] = useState<NFTSingleOrder[]>([]);
  const [singleListings, setSingleListings] = useState<NFTSingleOrder[]>([]);
  const [poolOrders, setPoolOrders] = useState<NFTPoolOrder[]>([]);
  const [escrowAccounts, setEscrowAccounts] = useState<NFTEscrowAccount[]>([]);
  const { priceInUSD, marketsInitialised } = useMarkets();

  const solPriceUSD = useMemo(() => {
    if (marketsInitialised) {
      return priceInUSD(WRAPPED_SOL_MINT.toBase58());
    }
  }, [marketsInitialised, priceInUSD]);

  const tensorOrdersFetcher = useMemo(() => {
    if (wallet) {
      return new TensorOrdersFetcher({ connection, wallet });
    }
  }, [connection, wallet]);

  useEffect(() => {
    if (tensorOrdersFetcher && solPriceUSD) {
      tensorOrdersFetcher.solPriceUSD = solPriceUSD;
      tensorOrdersFetcher.fetchOpenBids().then(setOpenBids).catch(handleTensorError);

      tensorOrdersFetcher.fetchSingleListings().then(setSingleListings).catch(handleTensorError);

      tensorOrdersFetcher
        .fetchPools()
        .then(({ poolOrders, escrowAccounts }) => {
          setPoolOrders(poolOrders);
          setEscrowAccounts(escrowAccounts);
        })
        .catch(handleTensorError);
    } else {
      setOpenBids([]);
      setSingleListings([]);
      setPoolOrders([]);
      setEscrowAccounts([]);
    }
  }, [solPriceUSD, tensorOrdersFetcher]);

  const singleOrders = useMemo(() => [...openBids, ...singleListings], [openBids, singleListings]);

  return {
    singleOrders,
    poolOrders,
    escrowAccounts,
  };
};
